/**
 * Remote access for the home page reference listing, shared through
 * `useModel('homePageContext')` and consumed by `pages/v2/Reference/HomePageList`.
 *
 * Deliberately cache-free, unlike `influencerContext`: this backs a curation
 * screen where the list must reflect what the server currently holds, and where
 * the same record is rarely requested twice.
 *
 * Every call takes `success`/`failed` callbacks instead of returning a value,
 * passes `skipErrorHandler: true` so a failure does not raise a global toast on
 * a screen that renders its own empty/error state, and treats a transport error
 * and a `success: false` body identically.
 *
 * One of four near-identical siblings (home / wiki / social / alias); a change
 * to the shape here almost certainly belongs in the other three too.
 */
// import { useState } from 'react';
import {
  getV2HomePagesByInfluencersCount,
  getV2HomePages,
  countV2HomePages,
  markHomePageValidity,
} from '@/services/rest/api';

export default () => {
  const doGetHomePagesByInfluencersCount = async (
    success: (homePages: DATA.HomePage[]) => void,
    failed: () => void,
  ) => {
    try {
      const msg = await getV2HomePagesByInfluencersCount({ skipErrorHandler: true });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error fetching home pages:', error);
      failed();
    }
  };

  const doGetHomePages = async (
    success: (homePages: DATA.HomePage[]) => void,
    failed: () => void,
    isInvalid?: boolean,
    lastId?: string,
  ) => {
    try {
      const msg = await getV2HomePages(isInvalid, lastId, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error fetching home pages:', error);
      failed();
    }
  };

  const doCountHomePages = async (
    success: (count: number) => void,
    failed: () => void,
    isInvalid?: boolean,
  ) => {
    try {
      const msg = await countV2HomePages(isInvalid, { skipErrorHandler: true });
      if (msg.success && typeof msg.data === 'number') {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error count home pages:', error);
      failed();
    }
  };

  const doMarkHomePageValidity = async (
    success: (obj: {id: string, isInvalid: boolean}) => void,
    failed: () => void,
    id: string,
    isInvalid: boolean,
  ) => {
    try {
      const msg = await markHomePageValidity(id, isInvalid, { skipErrorHandler: true });
      if (msg.success && msg.data) {
        success(msg.data);
      } else {
        failed();
      }
    } catch (error) {
      console.error('Error mark home page validity:', error);
      failed();
    }
  };

  return {
    doGetHomePagesByInfluencersCount,
    doGetHomePages,
    doCountHomePages,
    doMarkHomePageValidity,
  };
};
